import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { css, NixieVivo } from '../components/UI';

const dos = (n) => String(n).padStart(2, '0');
const lecturaFecha = (d) => `${dos(d.getDate())}.${dos(d.getMonth() + 1)}.${d.getFullYear()}`;
const lecturaHora = (d) => `${dos(d.getHours())}:${dos(d.getMinutes())}:${dos(d.getSeconds())}`;

// Barrido de cátodos: al encender, un contador nixie recorre sus dígitos antes
// de asentarse. Cada posición se asienta un poco después que la anterior.
function barrido(real, paso, total) {
  return real.split('').map((c, i) => {
    if (!/\d/.test(c)) return c;
    const asienta = Math.round(total * (0.35 + 0.65 * (i / real.length)));
    return paso >= asienta ? c : String(Math.floor(Math.random() * 10));
  }).join('');
}

const sinMovimiento = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function Login() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // El panel es un instrumento vivo: fecha y hora reales, sin inventar cifras de
  // negocio que no existen antes de iniciar sesión.
  const [ahora, setAhora] = useState(() => new Date());
  const [paso, setPaso] = useState(() => (sinMovimiento() ? Infinity : 0));
  const PASOS = 18; // ~1.1 s de barrido a 60 ms por paso

  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (paso >= PASOS) return undefined;
    const t = setTimeout(() => setPaso(p => p + 1), 60);
    return () => clearTimeout(t);
  }, [paso]);

  const fecha = lecturaFecha(ahora);
  const hora = lecturaHora(ahora);
  const arrancando = paso < PASOS;

  // Los tubos acompañan el inicio de sesión: barren mientras se verifica la
  // cuenta, parpadean en rojo si falla y se apagan justo antes de entrar.
  const [fase, setFase] = useState('reposo'); // reposo | verificando | falla | apagando
  const [, setTic] = useState(0);
  useEffect(() => {
    if (fase !== 'verificando' || sinMovimiento()) return undefined;
    const t = setInterval(() => setTic(n => n + 1), 70);
    return () => clearInterval(t);
  }, [fase]);
  const espera = (ms) => new Promise(r => setTimeout(r, sinMovimiento() ? 0 : ms));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setFase('verificando');
    const inicio = Date.now();
    // El barrido dura al menos 0.6 s para que se lea, aunque el servidor conteste antes
    const minimo = () => espera(Math.max(0, 600 - (Date.now() - inicio)));
    try {
      await login(form.email, form.password, async () => { await minimo(); setFase('apagando'); await espera(460); });
    } catch (err) {
      await minimo();
      setError(err.message);
      setFase('falla');
      setTimeout(() => setFase(f => (f === 'falla' ? 'reposo' : f)), 950);
      setLoading(false);
    }
  };
  const barriendo = arrancando || (fase === 'verificando' && !sinMovimiento());
  const lectura = (real) => (arrancando ? barrido(real, paso, PASOS) : fase === 'verificando' && !sinMovimiento() ? barrido(real, 0, PASOS) : real);

  return (
    <div className="login-page">
      <div className="login-brand">
        <img src={`${process.env.PUBLIC_URL}/logo-selectshop.webp`} alt="Selectshop" width={188} height={28} style={{ display: 'block' }} />
        <span className="login-producto">Solicitudes de pago</span>
      </div>
      <div className="login-intro">
        <div className={`login-instrumento${fase === 'falla' ? ' nx-falla' : ''}${fase === 'apagando' ? ' nx-apagado' : ''}`} aria-busy={barriendo}>
          <div className="login-counter">
            <NixieVivo value={lectura(fecha)} size="inherit" label={`Hoy, ${ahora.toLocaleDateString('es-MX')}`} />
          </div>
          <div className="nx-label login-counter-cap">Día · Mes · Año</div>
          <div className="login-counter login-reloj">
            <NixieVivo value={lectura(hora)} size="inherit" label={`Son las ${hora.slice(0, 5)}`} />
          </div>
          <div className="nx-label login-counter-cap">Hora · Min · Seg</div>
        </div>
      </div>
      <section className="login-card" aria-labelledby="login-title">
        <h1 id="login-title">Iniciar sesión</h1>
        <p>Ingresa con tu cuenta de Selectshop</p>
        <form onSubmit={submit}>
          <div className="login-field">
            <label htmlFor="login-email" className="nx-label">Correo electrónico</label>
            <input id="login-email" type="email" autoComplete="username" required value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="usuario@empresa.com" style={{ ...css.input(false), padding: '12px 14px' }} />
          </div>
          <div className="login-field">
            <label htmlFor="login-password" className="nx-label">Contraseña</label>
            <input id="login-password" type="password" autoComplete="current-password" required value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Ingresa tu contraseña" style={{ ...css.input(false), padding: '12px 14px' }} />
          </div>
          {error && <div className="login-error" role="alert">{error}</div>}
          <button className="nx-plate login-submit" type="submit" disabled={loading}>{fase === 'apagando' ? 'Entrando…' : loading ? 'Verificando…' : 'Entrar'}</button>
        </form>
      </section>
      <div className="login-footer">© {new Date().getFullYear()} Selectshop · Gestión de solicitudes y pagos</div>
    </div>
  );
}
